import { faStar as farStar } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle, faStar as fasStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import {
    count_ANSWERED_QUESTIONS,
    num_POSTS_AND_ANSWERS_IN_LAST_WEEK,
    FORUM_READ_MORE,
    FORUM_TOPIC_SET_DESCRIPTION_AND_RULES,
    SUBSCRIBE_TOPIC,
    TOPIC_COUNSELLOR,
    TOPIC_HEADER_DONT_WANT_TO_BE_SUBSCRIBED,
} from 'mk/autogenerated/translations/TopicHeader.a1643b92b4955594c69e96c59dd59d2e'
import { isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import { COUNSELLING_CATEGORY_ID } from 'mk2/apps/forum/constants';
import { TOPIC_SUBSCRIBE_TRIGGER, TOPIC_UNSUBSCRIBE_TRIGGER } from 'mk2/apps/forum/containers/Topic/Topic.actions';
import { TopicViewType } from 'mk2/apps/forum/containers/Topic/TopicPage';
import { CounsellingEntity, TopicEntity } from 'mk2/apps/forum/schemas';
import { forumTopicEditUrl, forumTopicInfoUrl } from 'mk2/apps/forum/urls';
import { RenderSnippetAs } from 'mk2/apps/wiki/utils';
import { AvatarImg } from 'mk2/components/AvatarImg';
import { Btn,  BtnCountType, BtnType } from 'mk2/components/Btn';
import { Cover } from 'mk2/components/Cover';
import { Emojify } from 'mk2/components/Emojify';
import { ExpandableClamp } from 'mk2/components/ExpandableClamp';
import { ImgCropped, ImgCropMode } from 'mk2/components/ImgCropped';
import { Interpolate } from 'mk2/components/Interpolate';
import { LoadingPlaceholder } from 'mk2/components/LoadingPlaceholder';
import ActionButton from 'mk2/containers/ActionButton/ActionButton';
import { astToHtml, defaultFilterAdditionalData } from 'mk2/helpers/article_ast/filters';
import { cacheLast } from 'mk2/helpers/cache';
import { url } from 'mk2/helpers/urls';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { UserEntity } from 'mk2/schemas';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';
import styles from './TopicHeader.mscss';

const fasInfoCircleLg: FontAwesomeIconProps = { icon: faInfoCircle, size: 'lg' };

interface OwnProps {
    topic: TopicEntity;
    counselling: CounsellingEntity;
    counsellingPostsCount: number | null;
    topicLoadingState: LoadingState;
    isSubscribed: boolean;
    isFavorite: boolean;
    requestUser: UserEntity;
    isModerator: boolean;
    subscribers: number;
    isMobile: boolean;
    viewType: TopicViewType;

    onSubscribe(slug: string, username: string);
    onUnsubscribe(slug: string, username: string);
    onAddToFavorites(topicId: number, userId: number);
    onRemoveFromFavorites(topicId: number, userId: number);
}

export default class TopicHeader extends React.Component<OwnProps> {
    private astToHtmlCache = cacheLast(true);

    public render() {
        const { counselling, viewType } = this.props;

        const isAskAnExpert = viewType === TopicViewType.AskAnExpert;

        return (
            <div className={cx(styles.TopicHeader, counselling && styles['TopicHeader--separated'])}>
                <>
                    {this.renderCover()}
                    {isAskAnExpert ? this.renderFooterAskAnExpert() : this.renderFooter()}
                </>
            </div>
        );
    }

    private renderCover() {
        const { counsellingPostsCount, topic, viewType } = this.props;
        const isAskAnExpert = viewType === TopicViewType.AskAnExpert;

        let subtitle;
        if (isAskAnExpert) {
            if (counsellingPostsCount > 0) {
                subtitle = <Interpolate i18nKey={count_ANSWERED_QUESTIONS} count={counsellingPostsCount} />;
            }
        } else {
            if (topic.numRecentContributors > 0) {
                subtitle = (
                    <Interpolate
                        i18nKey={num_POSTS_AND_ANSWERS_IN_LAST_WEEK}
                        num={<span className="number">{topic.numRecentContributors}</span>}
                        count={topic.numRecentContributors}
                    />
                );
            }
        }

        return <Cover photo={topic.coverPhoto} title={topic.name} subtitle={subtitle} />;
    }

    private renderFooter() {
        const { topic, isSubscribed, isFavorite, requestUser, topicLoadingState, subscribers } = this.props;

        const isModerator = isAuthenticatedAs(
            requestUser,
            topic.topicModerators?.map((tm) => tm.user) || topic.moderators,
        );
        const nonFavorableTopic =
            topic.category.id === COUNSELLING_CATEGORY_ID ||
            ['Súkromné diskusie', 'Soukromé diskuze'].includes(topic.category.name);
        const topicLoaded = isSuccess(topicLoadingState);

        return (
            <div className={styles.TopicHeader__footer}>
                {isModerator && !topic.descriptionAST && !topic.deletedAt ? (
                    <Btn
                        key="set-description"
                        type={BtnType.BlueTextLight}
                        label={FORUM_TOPIC_SET_DESCRIPTION_AND_RULES}
                        icon={fasInfoCircleLg}
                        link={url(forumTopicEditUrl, { topicId: topic.id })}
                    />
                ) : (
                    <>
                        {!topic.deletedAt && isSubscribed && !nonFavorableTopic && (
                            <Btn
                                key="favorite"
                                type={BtnType.LightGreyOutline}
                                icon={isFavorite ? fasStar : farStar}
                                onClick={!isFavorite ? this.handleOnAddToFavorites : this.handleOnRemoveFromFavorites}
                                className={cx(styles.TopicHeader__favorite, styles.TopicHeader__noRightBorder, {
                                    [styles['TopicHeader__favorite--on']]: isFavorite,
                                })}
                                disabled={!topicLoaded}
                            />
                        )}

                        {!topic.deletedAt && (
                            <ActionButton
                                key="subscribe"
                                type={isSubscribed ? BtnType.LightGreyOutline : BtnType.BlueOutline}
                                label={interpolate(
                                    isSubscribed ? TOPIC_HEADER_DONT_WANT_TO_BE_SUBSCRIBED : SUBSCRIBE_TOPIC,
                                )}
                                count={isSubscribed ? undefined : subscribers}
                                countType={BtnCountType.Label}
                                triggerAction={isSubscribed ? TOPIC_UNSUBSCRIBE_TRIGGER : TOPIC_SUBSCRIBE_TRIGGER}
                                onClick={isSubscribed ? this.handleOnUnsubscribe : this.handleOnSubscribe}
                                classNames={{ label: styles.TopicHeader__bold }}
                                className={cx(
                                    styles.TopicHeader__subscribe,
                                    !topic.deletedAt &&
                                        isSubscribed &&
                                        !nonFavorableTopic &&
                                        styles.TopicHeader__noLeftBorder,
                                )}
                                canBeVisuallyDisabled={!isModerator}
                                disabled={isModerator}
                            />
                        )}

                        <Btn
                            key="about"
                            type={BtnType.BlueTextLight}
                            icon={fasInfoCircleLg}
                            link={url(forumTopicInfoUrl, { topicSlug: topic.slug })}
                            className={styles.TopicHeader__info}
                        />
                    </>
                )}
            </div>
        );
    }

    private renderFooterAskAnExpert() {
        const { topic, isSubscribed, requestUser, subscribers, counselling } = this.props;
        const isModerator = isAuthenticatedAs(
            requestUser,
            topic.topicModerators?.map((tm) => tm.user) || topic.moderators,
        );

        let bodyChunks: React.ReactChild[] = null;
        if (topic?.descriptionAST) {
            const additionalData = {
                ...defaultFilterAdditionalData,
                linkedObjects: topic.descriptionASTAdditionalData
                    ? topic.descriptionASTAdditionalData.linkedObjects
                    : {},
                renderSpecialWikiLinks: false,
                renderSnippetObjectsAs: RenderSnippetAs.HTML_IFRAME,
                rootDiv: false,
            };
            bodyChunks = this.astToHtmlCache(
                () => astToHtml(topic.descriptionAST, additionalData),
                topic.descriptionAST,
            );
        }

        const counsellor = counselling?.counsellors?.find(c => c.isActive);

        return counsellor ? (
            <div className={styles.TopicHeader__counsellingFooter}>
                <div className={styles.TopicHeader__rowWrapper}>
                    <div className={styles.TopicHeader__counsellor}>
                        {counselling ? (
                            <ImgCropped
                                width={50}
                                height={50}
                                className={styles.TopicHeader__counsellorAvatar}
                                photo={counsellor.avatarPhoto}
                                photoVersion="s150x150"
                                mode={ImgCropMode.Cover}
                            />
                        ) : (
                            <AvatarImg
                                size={50}
                                user={counsellor.user}
                                className={styles.TopicHeader__counsellorAvatar}
                            />
                        )}
                        <div className={styles.TopicHeader__counsellorDescription}>
                            <div className={styles.TopicHeader__counsellorName}>{counsellor.name}</div>
                            <div className={styles.TopicHeader__counsellorBadge}>{TOPIC_COUNSELLOR}</div>
                        </div>
                    </div>

                    {!isModerator && !topic.deletedAt && (
                        <ActionButton
                            key="subscribe"
                            type={isSubscribed ? BtnType.LightGreyOutline : BtnType.BlueOutline}
                            label={interpolate(
                                isSubscribed ? TOPIC_HEADER_DONT_WANT_TO_BE_SUBSCRIBED : SUBSCRIBE_TOPIC,
                            )}
                            count={isSubscribed ? undefined : subscribers}
                            countType={BtnCountType.Label}
                            triggerAction={isSubscribed ? TOPIC_UNSUBSCRIBE_TRIGGER : TOPIC_SUBSCRIBE_TRIGGER}
                            onClick={isSubscribed ? this.handleOnUnsubscribe : this.handleOnSubscribe}
                            classNames={{ label: styles.TopicHeader__bold }}
                        />
                    )}
                </div>

                <ExpandableClamp expandLabel={FORUM_READ_MORE} clamp={5}>
                    <div className={styles.TopicHeader__counsellingInfo}>
                        <Emojify size={17}>{bodyChunks}</Emojify>
                    </div>
                </ExpandableClamp>
            </div>
        ) : null;
    }

    private handleOnSubscribe = () => {
        const { topic, requestUser, onSubscribe } = this.props;
        onSubscribe(topic.slug, requestUser.username);
    };

    private handleOnUnsubscribe = () => {
        const { topic, requestUser, onUnsubscribe } = this.props;
        onUnsubscribe(topic.slug, requestUser.username);
    };

    private handleOnAddToFavorites = () => {
        const { topic, requestUser, onAddToFavorites } = this.props;
        onAddToFavorites(topic.id, requestUser.id);
    };

    private handleOnRemoveFromFavorites = () => {
        const { topic, requestUser, onRemoveFromFavorites } = this.props;
        onRemoveFromFavorites(topic.id, requestUser.id);
    };
}

interface PlaceholderOwnProps {
    isLast?: boolean;
}

export const TopicHeaderPlaceholder: React.StatelessComponent<PlaceholderOwnProps> = ({ isLast }) => (
    <div className={cx(styles.TopicHeader, styles.TopicHeaderPlaceholder)}>
        <div className={styles.TopicHeader__cover}>
            <div className={cx(styles.TopicHeader__cover_photo, styles.TopicHeaderPlaceholder__cover_photo)} />
            <div className={styles.TopicHeader__cover_text}>
                <LoadingPlaceholder className={styles.TopicHeader__cover_topic_name} width={180} height={20} />
                <LoadingPlaceholder className={styles.TopicHeader__cover_posts} width={130} height={14} />
            </div>
        </div>
        <div className={styles.TopicHeader__footer}>
            <div className={cx(styles.TopicHeader__footer__subscribe, styles.TopicHeaderPlaceholder__subscribe)}>
                <LoadingPlaceholder width={120} height={25} />
            </div>
            <div className={styles.TopicHeaderPlaceholder__about}>
                <LoadingPlaceholder width={65} height={25} />
            </div>
            {/*<div className={cx(styles.TopicHeader__footer__sort, styles.TopicHeaderPlaceholder__sort)}>*/}
            {/*<LoadingPlaceholder width={30} height={25}/>*/}
            {/*</div>*/}
        </div>
    </div>
);
TopicHeaderPlaceholder.displayName = 'TopicHeaderPlaceholder';
